import React, { useState, useEffect } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { auth, googleProvider, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { track } from "@vercel/analytics";
import { useAuth } from "../context/AuthContext"; // ✅ Import the hook
import "./Login.css";

import SocialSidebar from "../components/SocialSideBar";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  const ensureUserDoc = async (user) => {
    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        username: user.displayName || "",
        email: user.email || "",
        mobile: "",
        phoneNumber: user.phoneNumber || "",
        class: "",
        preparingFor: "",
        address: "",
        authMethod: user.providerData[0]?.providerId || "unknown",
        photoURL: user.photoURL || "",
        isAdmin: false,
        isSuperAdmin: false,
        dashboardAccess: { userManagement: false, dashboard: false, invoices: false },
        purchasedCourses: [],
        testsTaken: [],
        role: "student",
        createdAt: new Date().toISOString(),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setTermsError(true);
      setMessage("Please agree to the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    setTermsError(false);
    track("login_email_submit", { page: window.location.pathname, email });
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setMessage("Please verify your email before logging in.");
        return;
      }

      await ensureUserDoc(user);
      navigate("/profile");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleGoogleLogin = async () => {
    track("login_google_click", { page: window.location.pathname });
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await ensureUserDoc(result.user);
      navigate("/");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handlePasswordReset = async () => {
    track("login_password_reset", { page: window.location.pathname, email });
    if (!email) {
      setMessage("Please enter your email first.");
      return;
    }
    sendPasswordResetEmail(auth, email)
      .then(() => setMessage("Password reset email sent! Check your inbox."))
      .catch((error) => setMessage(error.message));
  };

  const toggleForgotPassword = () => {
    track("login_toggle_forgot_password", { page: window.location.pathname, isForgotPassword: !isForgotPassword });
    setIsForgotPassword(!isForgotPassword);
  };

  return (
    <div className="login-container">
      <SocialSidebar />
      <div className="login-box">
        <h2>{isForgotPassword ? "Reset Your Password" : "Unlock Your Potential"}</h2>
        <h1 className="brand-name">PrrepX Pathshala</h1>

        <button onClick={handleGoogleLogin} className="google-btn">
          <FcGoogle className="google-icon" /> Sign in with Google
        </button>

        <div className="or-divider">
          <span>Or</span>
        </div>

        {isForgotPassword ? (
          <>
            <div className="input-group">
              <FaUser className="icon" />
              <input
                type="email"
                placeholder="Enter your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button onClick={handlePasswordReset} className="login-btn">
              Send Password Reset Link
            </button>
            <p className="forgot-password" onClick={toggleForgotPassword}>
              ⬅ Back to Login
            </p>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <FaUser className="icon" />
              <input
                type="email"
                placeholder="Enter Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="input-group password-group">
              <FaLock className="icon" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter Your Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            <p className="forgot-password" onClick={toggleForgotPassword}>
              Forgot Password?
            </p>
            <label className={`terms-check-inline${termsError ? " terms-check-error" : ""}`}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => { setAgreedToTerms(e.target.checked); setTermsError(false); }}
              />
              <span>
                {termsError && <span className="terms-required">* </span>}
                I agree to the{" "}
                <a href="/terms-conditions" target="_blank" rel="noreferrer">Terms &amp; Conditions</a>
                {" "}and{" "}
                <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
              </span>
            </label>
            <button type="submit" className="login-btn">
              Login
            </button>
          </form>
        )}

        {message && <p className="message">{message}</p>}

        <p className="signup-text">
          Don’t have an account? <a href="/signup">Sign up</a>
        </p>
      </div>
    </div>
  );
};

export default Login;
